import Icon from '@atoms/Icon/Icon';
import type { IconName } from '@models/IIcon';
import type { ILink } from '@models/ILink';
import classNames from 'classnames';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  PropsWithChildren,
} from 'react';
import { getButtonClasses } from './Button.utils';

interface Props {
  readonly className?: string;
  readonly dataTestId?: string;
  readonly disabled?: boolean;
  readonly editHint?: string;
  readonly hasCustomStyling?: boolean;
  readonly icon?: IconName;
  readonly iconSize?: string;
  readonly interactionTheme?: 'grey-250' | 'grey-600';
  readonly isLightMode?: boolean;
  readonly link?: ILink;
  readonly secondary?: boolean;
  readonly secondaryBackgroundClassName?: string;
  readonly tabIndex?: number;
  readonly theme?: 'yellow' | 'black' | 'red';
  readonly linkCallback?: () => void;
  readonly onFocus?: () => void;
}

type ButtonProps = Props &
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

type AnchorProps = Props &
  DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;

const Button = ({
  children,
  className,
  dataTestId,
  disabled = false,
  editHint,
  hasCustomStyling,
  icon,
  iconSize = '18px',
  interactionTheme = 'grey-600',
  isLightMode,
  link,
  secondary = false,
  secondaryBackgroundClassName = 'bg-transparent',
  tabIndex,
  theme = 'yellow',
  linkCallback,
  onFocus,
  ...rest
}: PropsWithChildren<ButtonProps | AnchorProps>) => {
  const isBlack = theme === 'black';
  const isRed = theme === 'red';

  const iconColorClass = classNames('text-black-100', {
    'group-hover:text-white group-focus:text-white group-active:text-white':
      secondary && (isBlack || isRed),
  });

  const buttonClasses = getButtonClasses({
    className,
    disabled,
    hasCustomStyling,
    icon,
    interactionTheme,
    isLightMode,
    secondary,
    secondaryBackgroundClassName,
    theme,
  });

  return link?.url && (link?.name || children) && !disabled ? (
    <a
      {...(rest as AnchorProps)}
      className={buttonClasses}
      href={link.url}
      target={link.target}
      data-epi-property-name={editHint}
      onClick={linkCallback}
      data-testid={dataTestId}
      onFocus={onFocus}
      tabIndex={tabIndex}
      {...(icon === 'download' && { download: link.url })}
    >
      {children || link.name}
      {icon && <Icon name={icon} className={iconColorClass} size={iconSize} />}
    </a>
  ) : (
    <button
      {...(rest as ButtonProps)}
      className={buttonClasses}
      disabled={disabled}
      data-epi-property-name={editHint}
      data-testid={dataTestId}
      onFocus={onFocus}
      tabIndex={tabIndex}
    >
      {children}
      {icon && (
        <Icon
          name={icon}
          className="ml-2"
          colorClassName={iconColorClass}
          size={iconSize}
        />
      )}
    </button>
  );
};

export default Button;
